import axios from 'axios';

const BASE_URL = 'https://loyalty.bom.asia';
const API_BASE_URL = `${BASE_URL}/api/miniApp/checkin/registrations`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const userEventRegApi = {
  // Get user registrations
  getUserRegistrations: async (userId) => {
    try {
      const response = await api.get(`/user/${userId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting user registrations:', error);
      throw error;
    }
  },

  // Get user events
  getUserEvents: async (userId) => {
    try {
      const response = await api.get(`/user/${userId}/events`);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting user events:', error);
      throw error;
    }
  },

  // Get event registrations
  getEventRegistrations: async (eventId) => {
    try {
      const response = await api.get(`/event/${eventId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting event registrations:', error);
      throw error;
    }
  },

  // Delete user event registration
  deleteUserEventRegistration: async (userId, eventId) => {
    try {
      const response = await api.delete(`/user/${userId}/event/${eventId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting user event registration:', error);
      throw error;
    }
  },

  // Register user for event
  registerUserForEvent: async (userId, eventId, registrationData = {}) => {
    try {
      const response = await api.post(`/user/${userId}/event/${eventId}`, registrationData);
      return response.data;
    } catch (error) {
      console.error('❌ Error registering user for event:', error);
      throw error;
    }
  },

  // Update user event registration
  updateUserEventRegistration: async (userId, eventId, updateData) => {
    try {
      const response = await api.put(`/user/${userId}/event/${eventId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating user event registration:', error);
      throw error;
    }
  },

  // Get registration statistics
  getRegistrationStats: async (eventId) => {
    try {
      const response = await api.get(`/event/${eventId}/stats`);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting registration stats:', error);
      throw error;
    }
  },

  // Check if user is registered for event
  checkUserRegistration: async (userId, eventId) => {
    try {
      const response = await api.get(`/user/${userId}/event/${eventId}/check`);
      return response.data;
    } catch (error) {
      console.error('❌ Error checking user registration:', error);
      throw error;
    }
  },

  // Get all user event registrations (all users)
  getAllUserEventRegistrations: async (params = {}) => {
    try {
      
      // Build query string
      const queryParams = new URLSearchParams();
      
      // Pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Filters
      if (params.status) queryParams.append('status', params.status);
      if (params.eventId) queryParams.append('eventId', params.eventId);
      if (params.userId) queryParams.append('userId', params.userId);
      if (params.type) queryParams.append('type', params.type);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      
      // Sorting
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      
      const queryString = queryParams.toString();
      const url = queryString ? `/user/event/all?${queryString}` : '/user/event/all';
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting all user event registrations:', error);
      throw error;
    }
  }
};

export default userEventRegApi;
